// The one rule for putting a table row into a header's column order (grid-columns): the cells
// with a `data-column` follow `keys`, a cell without one (the leading checkbox) stays in front,
// then the filler cell, and the trailing (actions) cell goes last. Works on a <colgroup> too,
// whose <col>s carry the same keys.
//
// Idempotent, and it leaves a row that is already in order untouched: moving a cell that holds
// focus (an open editor, the draft's input) would blur it.
export function orderCells(row, keys) {
  const cells = [...row.children].filter((cell) => cell.dataset.column)
  if (cells.length === 0) return // the empty-state row is a single colspan cell

  const byKey = new Map(cells.map((cell) => [cell.dataset.column, cell]))
  const wanted = keys.filter((key) => byKey.has(key))
  const filler = [...row.children].find((cell) => "filler" in cell.dataset)
  const trailing = [...row.children].find((cell) => "trailing" in cell.dataset)
  const tail = [filler, trailing].filter(Boolean)
  const inOrder =
    wanted.length === cells.length &&
    wanted.every((key, index) => cells[index].dataset.column === key) &&
    tail.every((cell, index) => row.children[row.children.length - tail.length + index] === cell)
  if (inOrder) return

  wanted.forEach((key) => row.appendChild(byKey.get(key)))
  tail.forEach((cell) => row.appendChild(cell))
}

// A row rendered against another layout than the header's (see grid-columns' stream hook): a
// cell of a column the header does not show is dropped, a column the header shows but the row
// lacks gets an empty cell, so every cell still sits under its own header. `orderCells` then
// puts them in order.
export function fitCellsToHeader(row, keys) {
  const cells = [...row.children].filter((cell) => cell.dataset.column)
  if (cells.length === 0) return

  const present = new Set(cells.map((cell) => cell.dataset.column))
  cells.filter((cell) => !keys.includes(cell.dataset.column)).forEach((cell) => cell.remove())
  keys
    .filter((key) => !present.has(key))
    .forEach((key) => {
      const cell = row.ownerDocument.createElement("td")
      cell.dataset.column = key
      row.appendChild(cell)
    })
  orderCells(row, keys)
}
